import React, { useCallback } from 'react';

import { trackingEvent } from '@notino/web-tracking';

import { EStockAvailability } from '@model';
import { EcommerceSectionNamespace, trackFreeDeliveryStatus } from '@services/ecommerceService';
import { dispatchEcommerceMessage, dispatchMessage, MessagingEvents } from '@services/messagingService';
import { hasFreeDeliveryForCart } from '@utils/delivery';
import { getTrackingProductName } from '@utils/tracking';

import { createEcommerceProduct, createModifyCartEvent, useTrackModifyCartEvent } from '../../../ecommerce';
import { usePutProductAwaitable } from '../../../services';
import { useUpdateItemQuantityCheck } from '../hooks';
import { CartActions, ECartActions, IUpdateItemInCartAction } from '../model';

export const useUpdateItem = (dispatch: React.Dispatch<CartActions>) => {
  const { request } = usePutProductAwaitable();
  const checkUpdatedQuantity = useUpdateItemQuantityCheck();
  const trackModifyCartEvent = useTrackModifyCartEvent();

  return useCallback(
    async (data: IUpdateItemInCartAction): Promise<boolean> => {
      const {
        product: { productId, itemId },
        product,
        newQuantity,
      } = data;

      const trackingCb = (quantity = newQuantity) => {
        if (product.count !== quantity) {
          trackModifyCartEvent({
            ...createModifyCartEvent(product.count, quantity),
            product: createEcommerceProduct(product),
          });
        }
      };

      const hadFreeDelivery = hasFreeDeliveryForCart(
        data.minimalPriceLimit,
        data.freeDelivery,
        data.freeDeliveryGlobalLimit,
        data.freeDeliveryGlobalFrom,
        data.freeDeliveryGlobalTo,
        data.benefits,
        data.priceProductsSummary,
        data.discountWithoutDelivery,
      );
      const updateProductsResponse = await request({ productId, itemId, count: newQuantity });
      dispatchMessage({ event: MessagingEvents.ECartManipulationEvents.CART_UPDATE_EVENT });

      if (updateProductsResponse) {
        dispatch({ actionType: ECartActions.UPDATE_PRODUCTS, payload: updateProductsResponse.data });

        const gotFreeDelivery = hasFreeDeliveryForCart(
          data.minimalPriceLimit,
          data.freeDelivery,
          data.freeDeliveryGlobalLimit,
          data.freeDeliveryGlobalFrom,
          data.freeDeliveryGlobalTo,
          updateProductsResponse.data.benefits,
          updateProductsResponse.data.pricesSummary.priceProductsSummary,
          updateProductsResponse.data.pricesSummary.discountWithoutDelivery,
        );

        if (!hadFreeDelivery && gotFreeDelivery) {
          trackFreeDeliveryStatus();
        }
      } else {
        dispatchEcommerceMessage(
          trackingEvent({
            event: 'add_to_cart_failed',
            add: {
              products: [
                {
                  brand_name: product.brandName,
                  full_price: product.priceSumWithVat,
                  kind: product.kind,
                  price: product.priceWithVat,
                  primary_category: product.primaryCategories.category,
                  primary_subcategory: product.primaryCategories.subCategory,
                  primary_type: product.primaryCategories.type,
                  product_code: product.productCode,
                  product_name: getTrackingProductName({ ...product, productName: product.name }),
                  quantity: product.count,
                  cart_type: EcommerceSectionNamespace.CartType.cart,
                  collection: product.collection || '',
                  availability: product.stockAvailability !== EStockAvailability.OUT_OF_STOCK,
                },
              ],
            },
            error: {
              status: 'failed to update quantity',
            },
            _clear: true,
          }),
        );
      }
      return checkUpdatedQuantity(updateProductsResponse, productId, trackingCb);
    },
    [request, checkUpdatedQuantity, trackModifyCartEvent, dispatch],
  );
};
